// Types based on Hasura/PostgreSQL schema

export interface Person {
  id: string;
  uid?: string;
  familyIds: string[];
  createdAt: string;
  modifiedAt: string;
  data: PersonData;
  status?: string;
}

export interface PersonData {
  profile: PersonProfile;
}

export interface PersonProfile {
  first: string;
  last: string;
  email?: string;
  phone?: string;
  gender?: 'M' | 'F' | 'O';
  birthDate?: string;
  photo?: PhotoSource;
  photoURL?: string;
  profilePhotoId?: string;
  role?: string;
}

export interface PhotoSource {
  uri?: string;
  bucket?: string;
  key?: string;
}

export interface User {
  id: string;
  personId: string;
  familyIds: string[];
  createdAt: string;
  modifiedAt: string;
  data: UserData;
  status?: string;
}

export interface UserData {
  notifications?: {
    chat?: number;
  };
}

export interface Family {
  id: string;
  memberIds: string[];
  createdAt: string;
  modifiedAt: string;
  data: FamilyData;
  status?: string;
}

export interface FamilyData {
  name: string;
  members: Record<string, FamilyMember>;
}

export interface FamilyMember {
  id: string;
  color?: string;
  role?: string;
}

export interface FamilyPerson {
  id: string;
  profile: PersonProfile;
  families?: Record<string, FamilyMember>;
}

// Feature data types stored in familiesdata/peopledata
export interface Activity {
  id: string;
  name: string;
  memberIds?: string[];
}

export type ActivityScheduleType = 'practice' | 'game' | 'performance' | 'recital' | 'lesson' | 'other';

export type AssignmentType = 'homework' | 'test' | 'quiz' | 'project' | 'other';

export interface ClassAssignment {
  id: string;
  type: AssignmentType;
  name: string;
  dueDate?: string;
  description?: string;
  completed?: boolean;
}

export interface ActivitySchedule {
  id: string;
  scheduleType: ActivityScheduleType;
  location: string;
  isRecurring: boolean;
  days?: string[];
  time?: string;
  endTime?: string;
  date?: string;
  dateTime?: string;
  endDateTime?: string;
  startDate?: string;
  endDate?: string;
}

export interface School {
  id: string;
  name: string;
}

export interface SchoolClass {
  id: string;
  name: string;
  schoolId?: string;
}

export interface Medication {
  id: string;
  name: string;
  personId: string;
}

export interface Illness {
  id: string;
  name: string;
  personId: string;
}

export interface Allergy {
  id: string;
  name: string;
  personId: string;
}

export interface Chore {
  id: string;
  name: string;
}

export interface Routine {
  id: string;
  name: string;
}

export interface Contact {
  id: string;
  first: string;
  last: string;
  role?: string;
}

export interface Note {
  id: string;
  title: string;
  text: string;
}

export interface ListItem {
  id: string;
  title: string;
}

export interface Quote {
  id: string;
  text: string;
  author?: string;
}

export interface Album {
  id: string;
  name: string;
  media: Record<string, Media>;
}

export interface Media {
  id: string;
  mime?: string;
  uri?: string;
}

export interface AlbumFileEntry {
  id: string;
  type: 'image' | 'video' | 'file';
}

export function normalizeFileEntry(entry: string | AlbumFileEntry): AlbumFileEntry {
  if (typeof entry === 'string') return { id: entry, type: 'image' };
  return entry;
}

export interface AlbumData {
  name: string;
  coverFileId?: string;
  fileIds: AlbumFileEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface AlbumMediaFile {
  id: string;
  type: 'image' | 'file' | 'video' | 'audio' | 'link';
  uri: string;
  name?: string;
  mimeType?: string;
  size?: number;
  thumbnailUri?: string;
  width?: number;
  height?: number;
  miniPreview?: string;
  hasPreview?: boolean;
}

export interface MediaItem {
  id: string;
  uri: string;
  miniPreview?: string;
  width?: number;
  height?: number;
}

// Calendar types
export type CalendarType =
  | 'General'
  | 'Activities'
  | 'Education'
  | 'Health'
  | 'Money'
  | 'Shopping'
  | 'Discipline'
  | 'Childcare'
  | 'Custody';

export type RepeatType =
  | 'None'
  | 'Daily'
  | 'Weekly'
  | 'Bi-Weekly'
  | 'Monthly'
  | 'Yearly'
  | 'Custom';

export type AlertType =
  | 'None'
  | '0'
  | '300'
  | '900'
  | '1800'
  | '3600'
  | '7200'
  | '86400'
  | '172800'
  | '604800';

export interface EventLocation {
  address?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  details?: string[];
}

export interface EventHealthProvider {
  id: string;
  name: string;
  type?: string;
  subtype?: string;
}

export interface EventActivity {
  id: string;
  name: string;
  eventType?: string;
}

export interface EventSchool {
  id: string;
  name: string;
  classId?: string;
  className?: string;
}

export interface EventCaregiver {
  id: string;
  name: string;
  type?: string;
}

export interface Event {
  id: string;
  title: string;
  description?: string;
  location?: EventLocation | string;
  locationDetails?: string;
  startTime: number;
  endTime: number;
  duration?: number;
  isAllDay: boolean;
  calendar: CalendarType;
  attendees: Record<string, boolean>;
  families: string[];
  notes?: string;
  url?: string;
  alert?: AlertType;
  tags?: string[];

  // Recurrence
  rrule?: string;
  repeat?: RepeatType;

  // Relationships
  healthProvider?: EventHealthProvider;
  activity?: EventActivity;
  school?: EventSchool;
  caregiver?: EventCaregiver;

  // Internal fields for expanded events
  baseEvent?: Event;
  recurEvent?: Event;
  occurrenceDate?: string;
  days?: string[];

  // Custody-specific
  householdId?: string;
  custodyPlanId?: string;
  household?: string;
  color?: string;

  // Metadata
  type?: 'event';
  status?: string;
  modifiedBy?: string;
  createdAt?: number;
  isNative?: boolean;
}

export interface EventsByDay {
  [date: string]: Event[];
}

export interface EventsById {
  [id: string]: Event;
}

// Database types (snake_case, matching Hasura)
export type ParticipantStatus = 'invited' | 'accepted' | 'declined' | 'tentative';
export type ParticipantRole = 'organizer' | 'attendee';

export interface DbEventParticipant {
  id: string;
  event_id: string;
  person_id: string;
  status: ParticipantStatus;
  role: ParticipantRole;
  notes?: string;
  created_at: string;
  modified_at: string;
}

export interface DbEvent {
  id: string;
  title: string;
  description?: string;
  location?: EventLocation;
  start_time: string;
  end_time: string;
  is_all_day: boolean;
  calendar: CalendarType;
  families: Array<{ family_id: string }>;
  rrule?: string;
  repeat?: string;
  alert?: string;
  tags?: string[];
  notes?: string;
  url?: string;
  created_by: string;
  created_at: string;
  modified_at: string;
  deleted_at?: string;
  data: Record<string, any>;
  participants?: DbEventParticipant[];
}

export interface CreateEventInput {
  title: string;
  description?: string;
  location?: EventLocation;
  start_time: string;
  end_time: string;
  is_all_day?: boolean;
  calendar?: CalendarType;
  family_ids: string[];
  rrule?: string;
  repeat?: string;
  alert?: string;
  tags?: string[];
  notes?: string;
  url?: string;
  created_by: string;
  data?: Record<string, any>;
  participants?: ParticipantInput[];
}

export interface UpdateEventInput {
  title?: string;
  description?: string;
  location?: EventLocation;
  start_time?: string;
  end_time?: string;
  is_all_day?: boolean;
  calendar?: CalendarType;
  rrule?: string;
  repeat?: string;
  alert?: string;
  tags?: string[];
  notes?: string;
  url?: string;
  data?: Record<string, any>;
}

export interface ParticipantInput {
  person_id: string;
  status?: ParticipantStatus;
  role?: ParticipantRole;
  notes?: string;
}

export interface ScheduleState {
  events: {
    eventsByDay: EventsByDay;
    eventsById: EventsById;
    alerts: Record<string, Event>;
  };
  month: string;
  loading: boolean;
}

export interface CalendarFilter {
  calendar: Record<CalendarType, boolean>;
  people: Record<string, boolean>;
  text: string;
}

// Calendar configuration
export const CALENDARS: Array<{ label: string; key: CalendarType; icon: string }> = [
  { label: 'General', key: 'General', icon: 'calendar' },
  { label: 'Activities', key: 'Activities', icon: 'bicycle' },
  { label: 'Education', key: 'Education', icon: 'graduation-cap' },
  { label: 'Health', key: 'Health', icon: 'stethoscope' },
  { label: 'Money', key: 'Money', icon: 'usd' },
  { label: 'Shopping', key: 'Shopping', icon: 'shopping-cart' },
  { label: 'Discipline', key: 'Discipline', icon: 'trophy' },
  { label: 'Childcare', key: 'Childcare', icon: 'child' },
];

export const REPEAT_OPTIONS: Array<{ key: RepeatType; label: string }> = [
  { key: 'None', label: 'None' },
  { key: 'Daily', label: 'Daily' },
  { key: 'Weekly', label: 'Weekly' },
  { key: 'Bi-Weekly', label: 'Bi-Weekly' },
  { key: 'Monthly', label: 'Monthly' },
  { key: 'Yearly', label: 'Yearly' },
  { key: 'Custom', label: 'Custom' },
];

export const ALERT_OPTIONS: Array<{ key: AlertType; label: string }> = [
  { key: 'None', label: 'None' },
  { key: '0', label: 'At time of event' },
  { key: '300', label: '5 minutes before' },
  { key: '900', label: '15 minutes before' },
  { key: '1800', label: '30 minutes before' },
  { key: '3600', label: '1 hour before' },
  { key: '7200', label: '2 hours before' },
  { key: '86400', label: '1 day before' },
  { key: '172800', label: '2 days before' },
  { key: '604800', label: '1 week before' },
];

export const ALERT_LABELS: Record<AlertType, string> = {
  'None': 'None',
  '0': 'At time of event',
  '300': '5 minutes before',
  '900': '15 minutes before',
  '1800': '30 minutes before',
  '3600': '1 hour before',
  '7200': '2 hours before',
  '86400': '1 day before',
  '172800': '2 days before',
  '604800': '1 week before',
};

export interface MoneyTally {
  owedByMe: number;
  owedToMe: number;
}

// Chat/Messaging types
export interface ChatMessage {
  id: string;
  threadId: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: number;
  attachments?: ChatAttachment[];
  mentions?: MentionData[];
  reactions?: Record<string, string[]>;
  replyTo?: string;
  edited?: boolean;
  editedAt?: number;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
}

export interface ChatAttachment {
  id: string;
  type: 'image' | 'file' | 'video' | 'link';
  uri: string;
  bucket?: string;
  key?: string;
  name?: string;
  size?: number;
  mimeType?: string;
  thumbnail?: string;
  thumbnailUri?: string;
  width?: number;
  height?: number;
  miniPreview?: string;
  hasPreview?: boolean;
}

export interface MentionData {
  personId: string;
  name: string;
  offset: number;
  length: number;
}

export interface ChatThread {
  id: string;
  type: 'family' | 'direct' | 'group';
  name?: string;
  familyIds: string[];
  participants: Record<string, boolean>;
  participantNames?: Record<string, string>;
  lastMessage?: ChatMessage;
  unreadCount?: number;
  muted?: boolean;
  pinnedAt?: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  parentsOnly?: boolean;
}

export interface TypingIndicator {
  threadId: string;
  userId: string;
  userName: string;
  timestamp: number;
}

// Custody Calendar Types
export type CustodyScheme =
  | 'day'              // Every Other Day
  | 'xdays'            // Every X Days
  | 'week'             // Every Other Week
  | 'xweeks'           // Every X Weeks
  | 'weekends'         // Every Other Weekend
  | 'xweekends'        // Every X Weekends
  | 'weekend_splitweek' // Every Other Weekend / Split Week
  | 'weekend_weekdays' // Weekend / Weekdays
  | 'alternating'      // Alternating Splits
  | '223';             // 2-2-3 Schedule

export type WeekdayName = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';

export interface Household {
  id: string;
  name: string;
  color: string;
  memberIds?: string[];
}

export type HouseholdType = 'single' | 'split';

export interface FamilyHouseholdData {
  householdType: HouseholdType;
  households?: Record<string, Household>;
  swapRequests?: SwapRequest[];
}

export interface HouseholdAssignment {
  householdId: string;
  color: string;
}

export type WeekSplit = {
  SU?: HouseholdAssignment;
  MO?: HouseholdAssignment;
  TU?: HouseholdAssignment;
  WE?: HouseholdAssignment;
  TH?: HouseholdAssignment;
  FR?: HouseholdAssignment;
  SA?: HouseholdAssignment;
};

export interface CustodyPlan {
  id: string;
  familyId: string;
  custodyScheme: CustodyScheme;
  custodySchemeText: string;
  startDate: number;
  endDate: number;
  startHousehold: string;
  startHouseholdName: string;
  swapDayOfWeek?: WeekdayName;
  swapDayOfMonth?: number;
  weekendStart?: WeekdayName;
  weekendEnd?: WeekdayName;
  xDays?: number;
  xWeeks?: number;
  xMonths?: number;
  xWeekends?: number;
  splits?: WeekSplit[];
  swapDates: Record<string, string>;
  submitter?: string;
  approved: boolean;
  comment?: string;
  versions?: CustodyPlanVersion[];
  createdAt?: number;
  modifiedAt?: number;
}

export interface CustodyPlanVersion {
  id: string;
  custodyScheme: CustodyScheme;
  startDate: number;
  endDate: number;
  startHousehold: string;
  swapDates: Record<string, string>;
  splits?: WeekSplit[];
  createdAt?: number;
}

export interface CustodyDate {
  date: string;
  householdId: string;
  household: string;
  color: string;
  custodyPlanId: string;
  isSwapped?: boolean;
  startTime: number;
  isAllDay: boolean;
}

export interface CustodyState {
  custodyScheme: CustodyScheme | null;
  custodySchemeText: string;
  custodyPlanId: string | null;
  startDate: Date;
  endDate: Date;
  lastPlanEndDate: Date | null;
  startHousehold: string | null;
  startHouseholdName: string;
  swapDayOfWeek: WeekdayName;
  swapDayOfMonth: number;
  weekendStart: WeekdayName;
  weekendEnd: WeekdayName;
  xDays: number;
  xWeeks: number;
  xMonths: number;
  xWeekends: number;
  splits: WeekSplit[];
  swapDates: Record<string, string>;
  isParametersComplete: boolean;
  ruleDescription: string;
  approved: boolean;
  comment?: string;
  submitter?: string;
  versions: CustodyPlanVersion[];
}

export interface SwapRequest {
  id: string;
  requestedBy: string;
  requestedByName: string;
  requestedAt: number;
  dates: string[];
  fromHouseholdId: string;
  toHouseholdId: string;
  reason?: string;
  status: 'pending' | 'approved' | 'denied';
  respondedBy?: string;
  respondedAt?: number;
  responseComment?: string;
}

export interface CustodyPlanWithId {
  id: string;
  plan: CustodyPlan;
  households: Record<string, Household>;
}

// Custody Configuration
export const CUSTODY_SCHEMES: Array<{ key: CustodyScheme; label: string; description: string }> = [
  { key: 'day', label: 'Every Other Day', description: 'Custody alternates daily' },
  { key: 'xdays', label: 'Every X Days', description: 'Custody changes every specified number of days' },
  { key: 'week', label: 'Every Other Week', description: 'Custody alternates weekly' },
  { key: 'xweeks', label: 'Every X Weeks', description: 'Custody changes every specified number of weeks' },
  { key: 'weekends', label: 'Every Other Weekend', description: 'Weekend custody alternates' },
  { key: 'xweekends', label: 'Every X Weekends', description: 'Weekend custody every specified number of weekends' },
  { key: 'weekend_splitweek', label: 'Every Other Weekend + Split Week', description: 'Alternating weekends with split weekdays' },
  { key: 'weekend_weekdays', label: 'Weekend / Weekdays', description: 'One household has weekends, other has weekdays' },
  { key: 'alternating', label: 'Alternating Splits', description: 'Custom alternating pattern with day-by-day control' },
  { key: '223', label: '2-2-3 Schedule', description: 'Two days, two days, three days — equal time over 2-week cycle' },
];

export const WEEKDAYS: Array<{ key: WeekdayName; label: string; short: string }> = [
  { key: 'Sunday', label: 'Sunday', short: 'SU' },
  { key: 'Monday', label: 'Monday', short: 'MO' },
  { key: 'Tuesday', label: 'Tuesday', short: 'TU' },
  { key: 'Wednesday', label: 'Wednesday', short: 'WE' },
  { key: 'Thursday', label: 'Thursday', short: 'TH' },
  { key: 'Friday', label: 'Friday', short: 'FR' },
  { key: 'Saturday', label: 'Saturday', short: 'SA' },
];

export const WEEKDAY_MAP: Record<WeekdayName, number> = {
  'Sunday': 0,
  'Monday': 1,
  'Tuesday': 2,
  'Wednesday': 3,
  'Thursday': 4,
  'Friday': 5,
  'Saturday': 6,
};

export const DEFAULT_HOUSEHOLD_COLORS = [
  '#6366F1', // Indigo
  '#F59E0B', // Amber
  '#10B981', // Emerald
  '#EF4444', // Red
  '#8B5CF6', // Violet
  '#06B6D4', // Cyan
];

// App state types
export interface AppStats {
  events: number;
  messages: number;
  notes: number;
  lists: number;
  photos: number;
  videos: number;
  quotes: number;
  contacts: number;
  activities: number;
  schools: number;
  classes: number;
  illnesses: number;
  allergies: number;
  doses: number;
  chores: number;
  routines: number;
  tally: MoneyTally;
}
